import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../LLM/index.js'

/**
 * Search trending topics with Linkup service and enhance with LLM processing
 * @param {string} query - Search query
 * @param {string} fromDate - Start date in ISO format (e.g., "2025-07-23T00:00:00+07:00")
 * @param {string} toDate - End date in ISO format (e.g., "2025-07-23T23:59:59+07:00")
 * @param {string} region - Region filter ('Indonesia' or 'International')
 * @param {Object} llmService - LLM service instance for answer enhancement
 * @returns {Promise<Object>} Trending search result with sources and content
 */
export const searchTrendingTopics = async (query, fromDate, toDate, region = 'Indonesia', llmService = null) => {
  try {
    console.log(`🔥 Starting trending search for:`, query)
    console.log(`📅 Date range: ${fromDate} to ${toDate}`)
    console.log(`🌍 Region: ${region}`)
    
    // Call linkup service with trending-specific parameters
    const linkupResult = await linkupService.searchTrending(
      query,
      fromDate,
      toDate,
      region
    )
    
    console.log(`✅ Trending search completed:`, {
      answerLength: linkupResult.answer?.length || 0,
      sourcesCount: linkupResult.sources?.length || 0,
      imagesCount: linkupResult.images?.length || 0
    })
    
    // Process and format the trending results
    const trendingItems = processTrendingResults(linkupResult, query)
    
    let enhancedAnswer = linkupResult.answer || 'Tidak dapat menemukan informasi trending untuk periode yang dipilih.'
    
    // Enhance answer with LLM if service is available and we have sources
    if (llmService && linkupResult.sources && linkupResult.sources.length > 0) {
      try {
        console.log('🤖 Enhancing trending analysis with GENIUS AI model...')
        
        // Prepare sources context for LLM
        const sourcesContext = linkupResult.sources.map((source, index) => 
          `[${index + 1}] ${source.title}\n${source.snippet}\nSumber: ${source.domain}\nURL: ${source.url}`
        ).join('\n\n')
        
        // Format date range for context
        const dateRangeText = formatDateRangeForLLM(fromDate, toDate)
        
        // Create comprehensive trending analysis prompt
        const trendingPrompt = `Sebagai ahli analisis tren digital, berikan analisis mendalam tentang topik trending berikut dengan minimal 1200-1500 kata.

TOPIK TRENDING: ${query}
PERIODE ANALISIS: ${dateRangeText}
REGION: ${region}

SUMBER INFORMASI TRENDING:
${sourcesContext}

INSTRUKSI ANALISIS KOMPREHENSIF:

## 1. RINGKASAN EKSEKUTIF (200-250 kata)
- Berikan overview singkat tentang mengapa topik ini trending
- Highlight poin-poin utama yang akan dibahas
- Sebutkan tingkat popularitas dan jangkauan trend

## 2. ANALISIS TREN UTAMA (400-500 kata)
- **Faktor Pemicu**: Apa yang menyebabkan topik ini menjadi trending?
- **Timeline Perkembangan**: Bagaimana trend ini berkembang dari waktu ke waktu?
- **Platform Dominan**: Di platform mana trend ini paling populer?
- **Demografi Target**: Siapa yang paling tertarik dengan trend ini?

## 3. DAMPAK DAN IMPLIKASI (300-400 kata)
- **Dampak Sosial**: Bagaimana trend ini mempengaruhi masyarakat?
- **Dampak Ekonomi**: Adakah implikasi bisnis atau ekonomi?
- **Dampak Budaya**: Bagaimana trend ini mempengaruhi budaya populer?
- **Reaksi Publik**: Bagaimana respons masyarakat terhadap trend ini?

## 4. ANALISIS KONTEN DAN ENGAGEMENT (200-250 kata)
- **Jenis Konten**: Apa format konten yang paling populer?
- **Tingkat Engagement**: Seberapa tinggi interaksi pengguna?
- **Viral Factor**: Apa yang membuat konten ini mudah dibagikan?
- **Influencer/Key Players**: Siapa tokoh penting dalam trend ini?

## 5. PREDIKSI DAN OUTLOOK (200-250 kata)
- **Durasi Trend**: Berapa lama trend ini diperkirakan bertahan?
- **Evolusi Potensial**: Bagaimana trend ini mungkin berkembang?
- **Trend Terkait**: Apa trend lain yang mungkin muncul?
- **Rekomendasi**: Saran untuk berbagai stakeholder

PERSYARATAN PENULISAN:
- Gunakan data dan fakta dari sumber yang disediakan
- Sertakan sitasi [1], [2], dst. untuk setiap klaim
- Gunakan bahasa yang engaging namun profesional
- Berikan insight yang actionable dan valuable
- Hindari spekulasi tanpa dasar
- Fokus pada analisis yang mendalam dan komprehensif

ANALISIS TRENDING KOMPREHENSIF:`
        
        const llmResponse = await llmService.sendMessage(
          [{ role: 'user', content: trendingPrompt }],
          { 
            mode: MODEL_MODES.GENIUS,
            maxTokens: 6000,
            searchType: 'trending_agent',
            stream: false,
            consumeCredits: false // Don't consume credits for answer enhancement
          }
        )
        
        if (llmResponse && llmResponse.trim().length > 0) {
          enhancedAnswer = llmResponse.trim()
          console.log('✅ Trending analysis enhanced with GENIUS AI model')
        }
        
      } catch (llmError) {
        console.warn('⚠️ Failed to enhance trending analysis with LLM, using original:', llmError)
        // Keep original answer if LLM enhancement fails
      }
    }
    
    return {
      answer: enhancedAnswer,
      sources: linkupResult.sources || [],
      images: linkupResult.images || [],
      trendingItems: trendingItems,
      searchQuery: query,
      dateRange: { fromDate, toDate },
      region: region,
      rawSourcesCount: linkupResult.rawSourcesCount || 0,
      validSourcesCount: linkupResult.validSourcesCount || 0,
      enhancedWithLLM: llmService && linkupResult.sources && linkupResult.sources.length > 0
    }
    
  } catch (error) {
    console.error(`❌ Error in trending search:`, error)
    
    // Return error result with fallback message
    return {
      answer: `Maaf, terjadi kesalahan saat mencari informasi trending. Silakan coba lagi nanti.`,
      sources: [],
      images: [],
      trendingItems: [],
      searchQuery: query,
      dateRange: { fromDate, toDate },
      region: region,
      error: error.message || 'Unknown error',
      rawSourcesCount: 0,
      validSourcesCount: 0,
      enhancedWithLLM: false
    }
  }
}

/**
 * Process trending results and extract top 10 trending items
 * @param {Object} linkupResult - Raw result from Linkup API
 * @param {string} query - Original search query
 * @returns {Array} Array of top 10 trending items
 */
const processTrendingResults = (linkupResult, query) => {
  if (!linkupResult.sources || linkupResult.sources.length === 0) {
    return []
  }
  
  // Group sources by topic/theme and extract trending themes
  const topicGroups = new Map()
  const processedSources = new Set()
  
  // Group sources by similar topics/keywords
  linkupResult.sources.forEach((source, index) => {
    const sourceKey = `${source.domain}_${source.title.substring(0, 50)}`
    if (processedSources.has(sourceKey)) return
    processedSources.add(sourceKey)
    
    const category = extractCategory(source.title, source.snippet)
    const keywords = extractKeywords(source.title, source.snippet)
    const topicKey = keywords.slice(0, 3).join('_') || category
    
    if (!topicGroups.has(topicKey)) {
      topicGroups.set(topicKey, {
        sources: [],
        category: category,
        keywords: keywords,
        totalRelevance: 0
      })
    }
    
    const relevanceScore = calculateRelevanceScore(source, query)
    topicGroups.get(topicKey).sources.push({ ...source, relevanceScore })
    topicGroups.get(topicKey).totalRelevance += relevanceScore
  })
  
  // Convert topic groups to trending items
  const trendingItems = []
  let itemId = 1
  
  // Sort topic groups by total relevance
  const sortedTopics = Array.from(topicGroups.entries())
    .sort(([,a], [,b]) => b.totalRelevance - a.totalRelevance)
    .slice(0, 10)
  
  sortedTopics.forEach(([topicKey, topicData]) => {
    const mainSource = topicData.sources[0] // Use the first source as main reference
    const sourceCount = topicData.sources.length
    
    // Create trending description based on topic analysis
    const trendingDescription = generateTrendingDescription(
      topicData.category,
      topicData.keywords,
      sourceCount,
      query
    )
    
    const trendingItem = {
      id: itemId++,
      title: generateTrendingTitle(topicData.keywords, topicData.category, mainSource.title),
      description: trendingDescription,
      url: mainSource.url || '#',
      domain: mainSource.domain || 'Multiple Sources',
      category: topicData.category,
      relevanceScore: Math.round(topicData.totalRelevance / sourceCount),
      publishedAt: extractPublishDate(mainSource) || new Date().toISOString(),
      sourceCount: sourceCount,
      isFallback: mainSource.isFallback || false
    }
    
    trendingItems.push(trendingItem)
  })
  
  return trendingItems
}

/**
 * Format date range for LLM context
 * @param {string} fromDate - Start date in ISO format
 * @param {string} toDate - End date in ISO format
 * @returns {string} Formatted date range text
 */
const formatDateRangeForLLM = (fromDate, toDate) => {
  try {
    const startDate = new Date(fromDate)
    const endDate = new Date(toDate)
    
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'Asia/Jakarta'
    }
    
    const startFormatted = startDate.toLocaleDateString('id-ID', options)
    const endFormatted = endDate.toLocaleDateString('id-ID', options)
    
    if (startFormatted === endFormatted) {
      return startFormatted
    }
    
    return `${startFormatted} hingga ${endFormatted}`
  } catch (error) {
    console.warn('Error formatting date range:', error)
    return `${fromDate} hingga ${toDate}`
  }
}

/**
 * Extract keywords from title and snippet
 * @param {string} title - Article title
 * @param {string} snippet - Article snippet
 * @returns {Array} Array of extracted keywords
 */
const extractKeywords = (title, snippet) => {
  const text = `${title} ${snippet}`.toLowerCase()
  const words = text.split(/\s+/)
  
  // Filter out common words and extract meaningful keywords
  const stopWords = new Set([
    'dan', 'atau', 'yang', 'ini', 'itu', 'dari', 'ke', 'di', 'pada', 'untuk',
    'dengan', 'oleh', 'dalam', 'akan', 'telah', 'adalah', 'sebagai', 'juga',
    'the', 'and', 'or', 'that', 'this', 'from', 'to', 'in', 'on', 'for',
    'with', 'by', 'will', 'has', 'is', 'as', 'also', 'a', 'an'
  ])
  
  const keywords = words
    .filter(word => word.length > 3 && !stopWords.has(word))
    .filter(word => /^[a-zA-Z\u00C0-\u017F\u0100-\u024F\u1E00-\u1EFF]+$/.test(word))
    .slice(0, 5)
  
  return [...new Set(keywords)] // Remove duplicates
}

/**
 * Extract category from title and snippet
 * @param {string} title - Article title
 * @param {string} snippet - Article snippet
 * @returns {string} Extracted category
 */
const extractCategory = (title, snippet) => {
  const text = `${title} ${snippet}`.toLowerCase()
  
  // Define category keywords
  const categories = {
    'Politik': ['politik', 'pemerintah', 'presiden', 'menteri', 'dpr', 'pemilu', 'pilkada', 'kebijakan'],
    'Ekonomi': ['ekonomi', 'bisnis', 'saham', 'investasi', 'bank', 'keuangan', 'inflasi', 'rupiah'],
    'Teknologi': ['teknologi', 'digital', 'ai', 'artificial intelligence', 'startup', 'aplikasi', 'software'],
    'Olahraga': ['sepak bola', 'basket', 'tenis', 'olimpiade', 'liga', 'pertandingan', 'atlet'],
    'Hiburan': ['film', 'musik', 'artis', 'selebriti', 'konser', 'drama', 'entertainment'],
    'Kesehatan': ['kesehatan', 'covid', 'virus', 'vaksin', 'rumah sakit', 'dokter', 'obat'],
    'Pendidikan': ['pendidikan', 'sekolah', 'universitas', 'mahasiswa', 'guru', 'kuliah'],
    'Sosial': ['sosial', 'masyarakat', 'komunitas', 'budaya', 'tradisi', 'agama']
  }
  
  // Find matching category
  for (const [category, keywords] of Object.entries(categories)) {
    if (keywords.some(keyword => text.includes(keyword))) {
      return category
    }
  }
  
  return 'Umum'
}

/**
 * Generate trending title based on keywords and category
 * @param {Array} keywords - Extracted keywords
 * @param {string} category - Article category
 * @param {string} originalTitle - Original article title
 * @returns {string} Generated trending title
 */
const generateTrendingTitle = (keywords, category, originalTitle) => {
  if (keywords.length === 0) {
    return originalTitle
  }
  
  const mainKeyword = keywords[0]
  const trendingPrefixes = {
    'Politik': 'Perkembangan Politik',
    'Ekonomi': 'Tren Ekonomi',
    'Teknologi': 'Inovasi Teknologi',
    'Olahraga': 'Sorotan Olahraga',
    'Hiburan': 'Buzz Hiburan',
    'Kesehatan': 'Update Kesehatan',
    'Pendidikan': 'Tren Pendidikan',
    'Sosial': 'Fenomena Sosial',
    'Umum': 'Trending Topic'
  }
  
  const prefix = trendingPrefixes[category] || 'Trending Topic'
  return `${prefix}: ${mainKeyword.charAt(0).toUpperCase() + mainKeyword.slice(1)}`
}

/**
 * Generate trending description based on analysis
 * @param {string} category - Article category
 * @param {Array} keywords - Extracted keywords
 * @param {number} sourceCount - Number of sources
 * @param {string} query - Original query
 * @returns {string} Generated trending description
 */
const generateTrendingDescription = (category, keywords, sourceCount, query) => {
  const keywordText = keywords.slice(0, 3).join(', ')
  
  const descriptions = {
    'Politik': `Topik politik yang sedang ramai dibicarakan terkait ${keywordText}. Mendapat perhatian dari ${sourceCount} sumber berita terpercaya.`,
    'Ekonomi': `Perkembangan ekonomi yang menjadi sorotan publik mengenai ${keywordText}. Dibahas di ${sourceCount} platform media.`,
    'Teknologi': `Inovasi dan perkembangan teknologi terbaru seputar ${keywordText}. Viral di ${sourceCount} sumber teknologi.`,
    'Olahraga': `Berita olahraga yang sedang trending tentang ${keywordText}. Menjadi perbincangan di ${sourceCount} media olahraga.`,
    'Hiburan': `Konten hiburan yang viral dan banyak dibicarakan terkait ${keywordText}. Trending di ${sourceCount} platform media.`,
    'Kesehatan': `Informasi kesehatan yang menjadi perhatian publik mengenai ${keywordText}. Dibahas di ${sourceCount} sumber kesehatan.`,
    'Pendidikan': `Tren pendidikan yang sedang disorot terkait ${keywordText}. Mendapat liputan dari ${sourceCount} media pendidikan.`,
    'Sosial': `Fenomena sosial yang viral dan banyak diperbincangkan tentang ${keywordText}. Trending di ${sourceCount} platform.`,
    'Umum': `Topik yang sedang trending dan banyak dibicarakan mengenai ${keywordText}. Viral di ${sourceCount} sumber media.`
  }
  
  return descriptions[category] || descriptions['Umum']
}

/**
 * Calculate relevance score based on various factors
 * @param {Object} source - Source object
 * @param {string} query - Search query
 * @returns {number} Relevance score (0-100)
 */
const calculateRelevanceScore = (source, query) => {
  let score = 50 // Base score
  
  const queryWords = query.toLowerCase().split(' ')
  const sourceText = `${source.title} ${source.snippet}`.toLowerCase()
  
  // Increase score for query word matches
  queryWords.forEach(word => {
    if (word.length > 2 && sourceText.includes(word)) {
      score += 10
    }
  })
  
  // Increase score for trusted domains
  const trustedDomains = [
    'kompas.com', 'detik.com', 'tribunnews.com', 'liputan6.com',
    'tempo.co', 'cnn.com', 'bbc.com', 'reuters.com'
  ]
  
  if (trustedDomains.some(domain => source.domain.includes(domain))) {
    score += 15
  }
  
  // Decrease score for fallback sources
  if (source.isFallback) {
    score -= 20
  }
  
  return Math.min(Math.max(score, 0), 100) // Clamp between 0-100
}

/**
 * Extract publish date from source (placeholder implementation)
 * @param {Object} source - Source object
 * @returns {string|null} ISO date string or null
 */
const extractPublishDate = (source) => {
  // This is a placeholder - in real implementation, you might extract
  // date from source metadata or content
  return null
}

/**
 * Get trending topics for today
 * @param {string} query - Search query
 * @param {string} region - Region filter
 * @returns {Promise<Object>} Trending search result
 */
export const getTodayTrending = async (query, region = 'Indonesia') => {
  const today = new Date()
  const fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString()
  const toDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString()
  
  return await searchTrendingTopics(query, fromDate, toDate, region)
}

/**
 * Get trending topics for a specific date
 * @param {string} query - Search query
 * @param {Date} date - Target date
 * @param {string} region - Region filter
 * @returns {Promise<Object>} Trending search result
 */
export const getDateTrending = async (query, date, region = 'Indonesia') => {
  const fromDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString()
  const toDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).toISOString()
  
  return await searchTrendingTopics(query, fromDate, toDate, region)
}

/**
 * Format trending items for display
 * @param {Array} trendingItems - Array of trending items
 * @returns {Array} Formatted trending items
 */
export const formatTrendingItems = (trendingItems) => {
  if (!trendingItems || !Array.isArray(trendingItems)) {
    return []
  }
  
  return trendingItems.map((item, index) => ({
    id: item.id || index + 1,
    title: item.title || 'Untitled Trending Topic',
    description: item.description || 'No description available',
    url: item.url || '#',
    domain: item.domain || 'Unknown',
    category: item.category || 'Umum',
    relevanceScore: item.relevanceScore || 0,
    publishedAt: item.publishedAt || new Date().toISOString(),
    isFallback: item.isFallback || false
  }))
}

/**
 * Get popular trending topics (predefined list)
 * @returns {Array} Array of popular trending topics
 */
export const getPopularTrendingTopics = () => {
  return [
    'berita trending terbaru hari ini',
    'politik terkini Indonesia',
    'ekonomi dan bisnis terbaru',
    'teknologi dan inovasi terbaru',
    'olahraga trending hari ini',
    'hiburan dan selebriti terbaru',
    'kesehatan dan gaya hidup',
    'pendidikan dan karir',
    'sosial dan budaya terkini',
    'lingkungan dan alam',
    'travel dan wisata trending',
    'fashion dan lifestyle terbaru'
  ]
}